/**
 * Drill-specific R3F overlay mounted inside Josh's TacticsBoard3D Canvas.
 *
 * Reads the drill canvas's live state (rolePlayers / cones / arrows /
 * pitch view) + animated playback output and renders 3D primitives that
 * mirror the 2D overlay. Shares the field + camera + lighting with the
 * tactics board.
 *
 * Composition (waves):
 *   - W3 DrillRolePlayer3D — player + ball capsules
 *   - W4 DrillArrow3D       — arrow tubes with kind/style mapping
 *   - W5 DrillCone3D        — equipment meshes (cones/mannequins/goals/etc.)
 *   - W6 DrillWorkingArea3D — dashed outline + grid for non-full views
 *   - W7 useDrillPlayback   — animation via useFrame mutation
 *   - W8 camera reframe     — handled at TacticsBoard3D level
 */
import { useEffect, useMemo } from 'react';
import type { DrillArrow, DrillCone } from '@4cm/core/drills/types';
import type { DrillRolePlayer } from './playerRoles';
import type { PitchSpec } from './pitchSpecs';
import type { PitchView, CustomPitch } from './pitchArea';
import type { AnimatedDrillState } from '../../lib/drillPlayback';
import { DrillRolePlayer3D } from './threeD/DrillRolePlayer3D';
import { DrillArrow3D } from './threeD/DrillArrow3D';
import { DrillCone3D } from './threeD/DrillCone3D';
import { DrillWorkingArea3D } from './threeD/DrillWorkingArea3D';
import { CameraReframer } from './threeD/CameraReframer';
import { drillNormToMetres3D } from '../../lib/drillTacticsAdapter';

export interface Drill3DLayerProps {
  rolePlayers: DrillRolePlayer[];
  cones: DrillCone[];
  arrows: DrillArrow[];
  /** Animated state from useDrillPlayback — when non-null, positions
   *  override the static rolePlayers/cones/arrows during playback. */
  animatedState: AnimatedDrillState | null;
  pitchSpec: PitchSpec;
  pitchView: PitchView;
  customPitch?: CustomPitch | null;
}

export function Drill3DLayer({
  rolePlayers,
  cones,
  arrows,
  animatedState,
  pitchSpec,
  pitchView,
  customPitch,
}: Drill3DLayerProps): React.JSX.Element {
  // Helper: convert a drill-norm [0..1] position to world metres,
  // respecting the visible region (full / half / quarter / custom).
  const toMetres = useMemo(
    () => (p: [number, number]) => drillNormToMetres3D(p, pitchSpec, pitchView, customPitch),
    [pitchSpec, pitchView, customPitch],
  );

  // Animated state wins during playback; otherwise render live state.
  const livePlayers = animatedState?.rolePlayers ?? rolePlayers;
  const liveCones = animatedState?.cones ?? cones;
  const liveArrows = animatedState?.arrows ?? arrows;

  // Test-only: expose counts on window so Playwright can assert that the
  // 3D layer mounted with the expected content. No-op in production
  // (Playwright tests check this; real users never see / care).
  useEffect(() => {
    (window as unknown as { __drill3DStats?: unknown }).__drill3DStats = {
      players: livePlayers.length,
      cones: liveCones.length,
      arrows: liveArrows.length,
      pitchView,
      hasAnimation: !!animatedState,
    };
  }, [livePlayers.length, liveCones.length, liveArrows.length, pitchView, animatedState]);

  return (
    <group name="drill-3d-layer">
      {/* Reframe the camera to fit the visible region (half / quarter /
          custom). Full view leaves Josh's tactical preset alone. */}
      <CameraReframer
        spec={pitchSpec}
        view={pitchView}
        custom={customPitch ?? null}
      />
      {/* Working-area outline / custom-grid plane. */}
      <DrillWorkingArea3D
        spec={pitchSpec}
        view={pitchView}
        custom={customPitch ?? null}
      />

      {/* Cones / equipment. */}
      {liveCones.map((c) => (
        <DrillCone3D
          key={c.id}
          cone={c}
          worldPosition={toMetres(c.position)}
        />
      ))}

      {/* Arrows — kind / style / colour propagate. */}
      {liveArrows.map((a) => {
        const from = a.from.kind === 'point' ? a.from.position : ([0.5, 0.5] as [number, number]);
        const to = a.to.kind === 'point' ? a.to.position : ([0.5, 0.5] as [number, number]);
        return (
          <DrillArrow3D
            key={a.id}
            arrow={a}
            fromWorld={toMetres(from)}
            toWorld={toMetres(to)}
          />
        );
      })}

      {/* Role players (incl. ball). */}
      {livePlayers.map((p) => (
        <DrillRolePlayer3D
          key={p.id}
          player={p}
          worldPosition={toMetres(p.position)}
        />
      ))}
    </group>
  );
}
